/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * Licensed under The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

import { useCallback, useEffect, useId, useRef } from 'preact/hooks';
import { preventDefault } from '../PRIVATE_SelectCommon';
import { HiddenAccessible } from '../UNSAFE_HiddenAccessible';
import { IconButton } from '../UNSAFE_IconButton';
import { Text } from '../UNSAFE_Text';
import { CloseIcon } from '../PRIVATE_ThemedIcons/CloseIcon';
import { TabbableModeContext } from '../hooks/UNSAFE_useTabbableMode';
import { useTranslationBundle } from '../hooks/UNSAFE_useTranslationBundle';
import { BundleType } from '../resources/nls/bundle';
import { isBackspaceOrDelete } from '../utils/PRIVATE_keyboardUtils';
import { classNames } from '../utils/UNSAFE_classNames';
import { textTagStyles } from './themes/TextTagStyles.css';

/**
 * Props for the TextTag component
 */
type Props<V> = {
  /**
   * The label for the text tag
   */
  children: string;

  /**
   * Specifies whether to show the remove icon
   */
  removeIcon?: 'always' | 'never';

  /**
   * Indicates whether the component is focused
   */
  isFocused?: boolean;

  /**
   * Specifies if the TextTag component is selected
   */
  isSelected?: boolean;

  /**
   * Callback to be invoked when Delete/Backspace is pressed
   */
  onRemoveAction?: (value: V) => void;

  /**
   * Callback to be invoked when clicking on the text tag
   */
  onSelect?: (value: V) => void;

  /**
   * Callback to be invoked when the remove icon is clicked
   */
  onRemoveIconClick?: (value: V) => void;

  /**
   * The value of the text tag
   */
  value: V;
};

/**
 * The component for rendering selectable/removable TextTag
 *
 * @param param0 The props for the TextTag component
 */
export function TextTag<V>({
  children,
  removeIcon = 'never',
  isSelected = false,
  onRemoveAction,
  onSelect,
  onRemoveIconClick,
  isFocused,
  value
}: Props<V>) {
  const translations = useTranslationBundle<BundleType>('@oracle/oraclejet-preact');
  const spanRef = useRef<HTMLSpanElement>(null);
  const navigationTextId = useId();

  const rootClasses = classNames([textTagStyles.base, isSelected && textTagStyles.selected]);

  const onRemoveActionCallback = useCallback(() => {
    onRemoveAction?.(value);
  }, [value, onRemoveAction]);
  const onSelectCallback = useCallback(() => {
    onSelect?.(value);
  }, [value, onSelect]);
  const onRemoveIconClickCallback = useCallback(() => {
    onRemoveIconClick?.(value);
  }, [value, onRemoveIconClick]);
  const onKeyUp = useCallback(
    (event: KeyboardEvent) => {
      if (isBackspaceOrDelete(event)) {
        onRemoveActionCallback();
        return;
      }
    },
    [onRemoveActionCallback]
  );

  useEffect(() => {
    if (isFocused && isSelected) {
      spanRef.current?.focus();
      spanRef.current?.scrollIntoView({ block: 'nearest' });
    }
  }, [isFocused, isSelected]);

  return (
    <>
      <span
        ref={spanRef}
        class={rootClasses}
        tabIndex={-1}
        onClick={onSelectCallback}
        onKeyUp={onKeyUp}
        // Kill mouse down event to prevent focus transfer done by the parent container.
        // We will programmatically take back focus in response to the onClick event.
        onMouseDown={preventDefault}
        role="option"
        aria-describedby={navigationTextId}
        aria-selected={isSelected ? 'true' : 'false'}>
        <Text size="md" variant="inherit">
          {children}
        </Text>
        {removeIcon === 'always' && (
          <TabbableModeContext.Provider value={{ isTabbable: false }}>
            <IconButton size="xs" variant="ghost" onAction={onRemoveIconClickCallback}>
              <CloseIcon />
            </IconButton>
          </TabbableModeContext.Provider>
        )}
      </span>
      <HiddenAccessible id={navigationTextId}>
        {translations.selectMultiple_removeSelectedTagInstructionText()}
      </HiddenAccessible>
    </>
  );
}
